// Set global for compatibility
window.global ||= window;

// Core Vue imports
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// Plugins & Router
import router from './router'
import {i18nPlugin} from './plugins/i18n'
import Buefy from 'buefy'
import 'buefy/dist/buefy.css'
import '@fortawesome/fontawesome-free/css/all.css';
import '@fortawesome/fontawesome-free/css/fontawesome.css';

// Helpers & Mixins
import type { BuefyConfig } from 'buefy/src/types/components'
import { accessTokenName, hotjarPage, refreshTokenName } from './helpers/utils'
import vueClickOutsideElement from 'vue-click-outside-element'
import * as amplitude from '@amplitude/analytics-browser';
import globalMixin from '@/mixins/global.mixin'
import eventBus from './store/event-bus-singleton'

// Assets
import en_flag from '@/assets/img/flags/en.svg'
import de_flag from '@/assets/img/flags/de.svg'
import ru_flag from '@/assets/img/flags/ru.svg'

// Import App component last to avoid circular dependencies
import App from './App.vue';
import * as Sentry from '@sentry/vue'

// Create app instance
const app = createApp(App);

// --- Plugin Usage ---

app.use(createPinia());
app.use(router); // Use router
app.use(vueClickOutsideElement); // Use click outside helper
app.use(i18nPlugin); // Use i18n plugin

// Highcharts library & Configuration
import HighchartsVue from 'highcharts-vue'
import Highcharts from 'highcharts';
import stockInit from 'highcharts/modules/stock';
import 'highcharts/css/highcharts.css'; // Import Highcharts CSS here
import { plugin as VueInputAutowidth } from 'vue-input-autowidth';
import highchartsAccessibility from "highcharts/modules/accessibility";
import { checkErrorFilter } from './helpers/sentry';

app.use(HighchartsVue); // Use HighchartsVue plugin
stockInit(Highcharts); // Initialize Highcharts Stock module
highchartsAccessibility(Highcharts); // Initialize Highcharts Accessibility module
app.use(VueInputAutowidth) // Use Input Autowidth plugin

// Buefy Plugin Usage
app.use(Buefy as any, {
	defaultIconPack: 'fas',
	defaultDateFormatter: (date: any) => {
		if (date.length === 0) {
			return null;
		}

		if (date.length && date.length > 1) {
			let return_data: string = '';
			date.forEach((element: any) => {
				return_data += new Intl.DateTimeFormat('en-GB').format(element) + ' - ';
			});

			return_data = return_data.substring(0, return_data.length - 3);

			return return_data;
		} else {
			return new Intl.DateTimeFormat('en-GB').format(date);
		}
	}
});


// --- Global Mixins ---
app.mixin(globalMixin);


// --- Global Properties & Config ---
if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
	try {
		amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {autocapture: { // Amplitude config
			attribution: true,
			pageViews: false,
			sessions: false,
			formInteractions: false,
			fileDownloads: false,
			elementInteractions: false,
		  }});
		app.config.globalProperties.$amplitude = amplitude; // Set global property
	} catch (err: any) {
		console.log('amplitude init error', err.toString())
	}
}


// Sentry.io error logging
if (import.meta.env.VITE_MODE === 'production') {
	try {
		Sentry.init({
			dsn: import.meta.env.VITE_SENTRY_ENDPOINT,
			app,
			replaysSessionSampleRate: 1,
			integrations: [],
			attachProps: true,
			release: 'app-frontend@' + import.meta.env.VITE_RELEASE_VERSION,
			beforeSend(event) {
				if (event.exception) {
					const exception = JSON.stringify(event.exception).toLowerCase();

					if (!checkErrorFilter(exception)) {
						//console.log('Error Filtered',exception)
						return null;
					}
				}

				return event;
			}
		});
	} catch (err: any) {
		console.log('Error initalising sentry - ' + err.toString())
	}
}


// Set Highcharts global options
Highcharts.setOptions({
	lang: {
		rangeSelectorZoom: '' // Customize Highcharts language options if needed
	},
	accessibility: {
		enabled: false, // Accessibility options
		keyboardNavigation: {
		  enabled: false,
		},
	  },
});


// Set Vue app performance flag based on environment
if (import.meta.env.VITE_MODE === 'production' || import.meta.env.VITE_MODE === 'staging') {
	app.config.performance = false;
} else {
	app.config.performance = true;
}


// --- Router Hooks ---
router.afterEach((to, from) => {
	eventBus.$emit('page_view', to); // Emit page view event
});

router.beforeEach((to, from, next) => {
	hotjarPage(to.fullPath); // Track page with Hotjar

	// Authentication guard logic
	const token = localStorage.getItem(accessTokenName);
	const refreshToken = localStorage.getItem(refreshTokenName);
	const publicRoutes = ['Reset', 'Login', 'Register', 'Unlock', 'AppLogin', 'RegisterInvited'];
	const isPublicRoute = publicRoutes.includes(to.name?.toString() || '');

	if (!token && !refreshToken && !isPublicRoute) {
		// Not logged in and accessing a protected route
		if (to.name !== 'Home') { // Avoid saving 'Home' as redirect target
			localStorage.setItem('logonRoute', to.fullPath);
		}
		next({ path: '/login', query: to.query || from.query }); // Redirect to login
	} else if (token && to.name === 'Auth' && to.params.authType !== 'reset') {
		// Logged in and trying to access auth pages (except reset)
		next({ path: '/', query: to.query || from.query }); // Redirect to home
	} else {
		// Allow navigation
		next();
	}
});


// --- Mount App ---
app.mount('#app');


// --- Language Helpers & Exports (Defined after mount) ---
export type TLanguage = {
	code: string;
	name: string;
	flag: string;
};
const activeLanguagesList: TLanguage[] = [
	{ code: 'en', name: 'English', flag: en_flag },
	{ code: 'de', name: 'Deutsch', flag: de_flag },
	{ code: 'ru', name: 'Русский', flag: ru_flag }
	// Other languages...
];
const getLanguageParameter = (languageCode: string, parameter: 'code' | 'name' | 'flag') => {
	let value: string = activeLanguagesList[0][parameter]; // Default to first language's parameter
	const findLang = activeLanguagesList.find((lang: TLanguage) => lang.code === languageCode);
	if (findLang) {
		value = findLang[parameter]; // Use found language's parameter
	}
	return value;
};

export { activeLanguagesList, getLanguageParameter }; // Export helpers
